import * as KnownProperties from "../../known-properties";
import * as OperationTimeGen2 from "../../../../operation-time-manager";
import { OpeningIcon } from "@genesys/ui-elements";
import { PropertyValue, PropertyValueSet } from "@genesys/property";
import { Amount, Units, Quantity } from "@genesys/uom";
import {
  MoistureHeatLoadTypeEnum,
  OperatingCaseGroupEnum
} from "../../../../graphql-types";

import {
  changedFromBoth0,
  changedToBoth0,
  getIfIntegerChanged,
  getIfPropertyChanged
} from "../../helper-functions";

//Icon
export const Icon = OpeningIcon;

// Visibility
const visibleClaimValue = 1;
export function getVisibility(moistureLoadClaimValue: number): boolean {
  return moistureLoadClaimValue >= visibleClaimValue;
}

// Moisture load selection
export const useMoistProperty = KnownProperties.useOpeningMoistureLoad;
const moistureLoadSelectClaimValue = 1;
export function getMoistureLoadSelectionVisibility(
  moistureLoadClaimValue: number
): boolean {
  return moistureLoadClaimValue >= moistureLoadSelectClaimValue;
}

// Heat load selection
const heatLoadSelectClaimValue = 1;
export const useHeatProperty = KnownProperties.useOpeningDoorHeatLoad;
export function getHeatLoadSelectionVisibility(
  moistureLoadClaimValue: number
): boolean {
  return moistureLoadClaimValue >= heatLoadSelectClaimValue;
}

export const sourceName = MoistureHeatLoadTypeEnum.OPENING;
export const description = "mlcopeningexplanation";
export const title = "mlcopening";

export const manualInputProperty = KnownProperties.openingManualInput;

export const properties = [
  KnownProperties.useOpeningMoistureLoad,
  KnownProperties.useOpeningDoorHeatLoad,
  KnownProperties.openingManualInput,
  KnownProperties.numberOfOpeningsPerHour,
  KnownProperties.openingDuration,
  KnownProperties.openingTotalDuration,
  KnownProperties.numberOfOpenings,
  KnownProperties.setOpeningArea,
  KnownProperties.openingWidth,
  KnownProperties.openingHeight,
  KnownProperties.openingArea,
  KnownProperties.openingAirVelocityMode,
  KnownProperties.openingAirVelocityCoefficient,
  KnownProperties.openingManualAirVelocity,
  KnownProperties.openingMaxAirVelocity,
  KnownProperties.openingAirSource,
  KnownProperties.openingAirTemperature,
  KnownProperties.openingUseOutdoorAirHumidity,
  KnownProperties.openingAirHumidity
];

export const overridableProperties = [
  KnownProperties.numberOfOpeningsPerHour,
  KnownProperties.openingDuration,
  KnownProperties.openingTotalDuration,
  KnownProperties.numberOfOpenings,
  KnownProperties.openingAirVelocityCoefficient,
  KnownProperties.openingManualAirVelocity,
  KnownProperties.openingAirTemperature,
  KnownProperties.openingAirHumidity
];

export const caseOverridesDefault: {
  readonly [operatingCaseGroup in OperatingCaseGroupEnum]: PropertyValueSet.PropertyValueSet;
} = {
  JANUARY: PropertyValueSet.Empty,
  FEBRUARY: PropertyValueSet.Empty,
  MARCH: PropertyValueSet.Empty,
  APRIL: PropertyValueSet.Empty,
  MAY: PropertyValueSet.Empty,
  JUNE: PropertyValueSet.Empty,
  JULY: PropertyValueSet.Empty,
  AUGUST: PropertyValueSet.Empty,
  SEPTEMBER: PropertyValueSet.Empty,
  OCTOBER: PropertyValueSet.Empty,
  NOVEMBER: PropertyValueSet.Empty,
  DECEMBER: PropertyValueSet.Empty
};

export function getApplicationTypeUpdates(
  applicationType: number
): ReadonlyArray<{
  readonly propertyName: string;
  readonly propertyValue: number;
}> {
  if (applicationType !== 11) {
    return [
      {
        propertyName: KnownProperties.useOpeningMoistureLoad,
        propertyValue: 0
      },
      {
        propertyName: KnownProperties.useOpeningDoorHeatLoad,
        propertyValue: 0
      }
    ];
  }

  return [];
}

export function getPropertyUpdates(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = getPropertyDefaults(previousSelections, currentSelections);

  return defaults;
}

export function getDefaultOperationTime(
  _currentSelections: PropertyValueSet.PropertyValueSet
) {
  return OperationTimeGen2.presets.factoryHours;
}

function getPropertyDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
) {
  let defaults = PropertyValueSet.Empty;

  defaults = PropertyValueSet.merge(
    useLoadDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    openingDurationDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    airSourceFromBuildingLocationDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    setOpeningAreaDefault(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );
  defaults = PropertyValueSet.merge(
    openingAreaDefaults(PropertyValueSet.merge(defaults, currentSelections)),
    defaults
  );

  defaults = PropertyValueSet.merge(
    openingAirVelocityModeDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    openingAirSourceDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );

  defaults = PropertyValueSet.merge(
    openingAirSourceDefaults(
      previousSelections,
      PropertyValueSet.merge(defaults, currentSelections)
    ),
    defaults
  );
  return defaults;
}

function useLoadDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  if (
    changedFromBoth0(
      previousSelections,
      currentSelections,
      useMoistProperty,
      useHeatProperty
    )
  ) {
    return {
      [KnownProperties.openingManualInput]: PropertyValue.fromInteger(0),
      [KnownProperties.numberOfOpeningsPerHour]: PropertyValue.fromInteger(1),
      [KnownProperties.openingDuration]: PropertyValue.fromAmount(
        Amount.create(30, Units.Second)
      ),
      [KnownProperties.setOpeningArea]: PropertyValue.fromInteger(0),
      [KnownProperties.numberOfOpenings]: PropertyValue.fromInteger(1),
      [KnownProperties.openingAirSource]: PropertyValue.fromInteger(1)
    };
  } else if (
    changedToBoth0(
      previousSelections,
      currentSelections,
      useMoistProperty,
      useHeatProperty
    )
  ) {
    return {
      [KnownProperties.openingManualInput]: PropertyValue.fromInteger(0),
      [KnownProperties.numberOfOpeningsPerHour]: PropertyValue.fromInteger(0),
      [KnownProperties.openingDuration]: PropertyValue.fromAmount(
        Amount.create(0, Units.Second)
      ),
      [KnownProperties.setOpeningArea]: PropertyValue.fromInteger(0),
      [KnownProperties.numberOfOpenings]: PropertyValue.fromInteger(0),
      [KnownProperties.openingAirSource]: PropertyValue.fromInteger(0)
    };
  }
  return PropertyValueSet.Empty;
}

function openingDurationDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  // Room location
  const openingDuration = getIfPropertyChanged(
    previousSelections,
    currentSelections,
    KnownProperties.openingDuration
  );

  const numberOfOpeningsPerHour = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.numberOfOpeningsPerHour
  );

  if (openingDuration !== undefined || numberOfOpeningsPerHour !== undefined) {
    const openings =
      PropertyValueSet.getInteger(
        KnownProperties.numberOfOpeningsPerHour,
        currentSelections
      ) || 0;

    const duration = Amount.valueAs(
      Units.Second,
      PropertyValueSet.getAmount<Quantity.Duration>(
        KnownProperties.openingDuration,
        currentSelections
      ) || Amount.create<Quantity.Duration>(0, Units.Second)
    );
    defaults = PropertyValueSet.merge(
      {
        [KnownProperties.openingTotalDuration]: PropertyValue.fromAmount(
          Amount.create<Quantity.Duration>(openings * duration, Units.Second)
        )
      },
      defaults
    );
  }
  return defaults;
}

function airSourceFromBuildingLocationDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  // Room location
  const buildingLocation = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.buildingLocation
  );

  if (buildingLocation !== undefined) {
    defaults = PropertyValueSet.merge(
      {
        [KnownProperties.openingAirSource]: PropertyValue.fromInteger(1)
      },
      defaults
    );
  }
  return defaults;
}

function openingAreaDefaults(
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  const setOpeningArea = PropertyValueSet.getInteger(
    KnownProperties.setOpeningArea,
    currentSelections
  );

  if (setOpeningArea === 0) {
    const openingHeight = Amount.valueAs(
      Units.Meter,
      PropertyValueSet.getAmount<Quantity.Length>(
        KnownProperties.openingHeight,
        currentSelections
      ) || Amount.create<Quantity.Length>(0, Units.Meter)
    );
    const openingWidth = Amount.valueAs(
      Units.Meter,
      PropertyValueSet.getAmount<Quantity.Length>(
        KnownProperties.openingWidth,
        currentSelections
      ) || Amount.create<Quantity.Length>(0, Units.Meter)
    );

    return {
      [KnownProperties.openingArea]: PropertyValue.fromAmount(
        Amount.create(openingHeight * openingWidth, Units.SquareMeter)
      )
    };
  }
  return PropertyValueSet.Empty;
}

function openingAirVelocityModeDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  const openingAirVelocityMode = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.openingAirVelocityMode
  );

  if (openingAirVelocityMode === undefined) {
    return PropertyValueSet.Empty;
  }

  switch (openingAirVelocityMode) {
    case 0:
    default:
      return {
        [KnownProperties.openingAirVelocityCoefficient]:
          PropertyValue.fromAmount(Amount.create(0, Units.One)),
        [KnownProperties.openingManualAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0, Units.MeterPerSecond)
        ),
        [KnownProperties.openingMaxAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0, Units.MeterPerSecond)
        )
      };
    case 1:
      return {
        [KnownProperties.openingAirVelocityCoefficient]:
          PropertyValue.fromAmount(Amount.create(0, Units.One)),
        [KnownProperties.openingManualAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0.25, Units.MeterPerSecond)
        ),
        [KnownProperties.openingMaxAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0, Units.MeterPerSecond)
        )
      };
    case 2:
      return {
        [KnownProperties.openingAirVelocityCoefficient]:
          PropertyValue.fromAmount(Amount.create(0.3, Units.One)),
        [KnownProperties.openingManualAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0, Units.MeterPerSecond)
        ),
        [KnownProperties.openingMaxAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0, Units.MeterPerSecond)
        )
      };
    case 3:
      return {
        [KnownProperties.openingAirVelocityCoefficient]:
          PropertyValue.fromAmount(Amount.create(0.3, Units.One)),
        [KnownProperties.openingManualAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0.0, Units.MeterPerSecond)
        ),
        [KnownProperties.openingMaxAirVelocity]: PropertyValue.fromAmount(
          Amount.create(0.5, Units.MeterPerSecond)
        )
      };
  }
}

function setOpeningAreaDefault(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  const setOpeningArea = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.setOpeningArea
  );

  if (setOpeningArea === undefined) {
    return PropertyValueSet.Empty;
  }

  return setOpeningArea === 0
    ? {
        [KnownProperties.openingHeight]: PropertyValue.fromAmount(
          Amount.create(4, Units.Meter)
        ),
        [KnownProperties.openingWidth]: PropertyValue.fromAmount(
          Amount.create(3, Units.Meter)
        )
      }
    : {
        [KnownProperties.openingHeight]: PropertyValue.fromAmount(
          Amount.create(0, Units.Meter)
        ),
        [KnownProperties.openingWidth]: PropertyValue.fromAmount(
          Amount.create(0, Units.Meter)
        )
      };
}

function openingAirSourceDefaults(
  previousSelections: PropertyValueSet.PropertyValueSet,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  let defaults = PropertyValueSet.Empty;

  const openingAirSource = getIfIntegerChanged(
    previousSelections,
    currentSelections,
    KnownProperties.openingAirSource
  );

  if (openingAirSource !== undefined) {
    defaults = PropertyValueSet.merge(
      openingAirStateDefaults(openingAirSource),
      defaults
    );
  }

  let openingUseOutdoorAirHumidity = getIfIntegerChanged(
    previousSelections,
    PropertyValueSet.merge(defaults, currentSelections),
    KnownProperties.openingUseOutdoorAirHumidity
  );

  if (openingUseOutdoorAirHumidity !== undefined) {
    defaults = PropertyValueSet.merge(
      openingUseOutdoorAirHumidityDefaults(
        openingUseOutdoorAirHumidity,
        PropertyValueSet.merge(defaults, currentSelections)
      ),
      defaults
    );
  }

  return defaults;
}

function openingUseOutdoorAirHumidityDefaults(
  propertyValue: number,
  currentSelections: PropertyValueSet.PropertyValueSet
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 1:
    default:
      return {
        [KnownProperties.openingAirHumidity]: PropertyValue.fromAmount(
          Amount.create(0, Units.GramPerKilogram)
        )
      };
    case 0:
      return {
        [KnownProperties.openingAirHumidity]: PropertyValue.fromAmount(
          PropertyValueSet.getInteger(
            KnownProperties.openingAirSource,
            currentSelections
          ) === 3
            ? Amount.create(8, Units.GramPerKilogram)
            : Amount.create(0, Units.GramPerKilogram)
        )
      };
  }
}

function openingAirStateDefaults(
  propertyValue: number
): PropertyValueSet.PropertyValueSet {
  switch (propertyValue) {
    case 0:
    case 1:
    case 2:
    default:
      return {
        [KnownProperties.openingAirTemperature]: PropertyValue.fromAmount(
          Amount.create(0, Units.Celsius)
        ),
        [KnownProperties.openingUseOutdoorAirHumidity]:
          PropertyValue.fromInteger(0),
        [KnownProperties.openingAirHumidity]: PropertyValue.fromAmount(
          Amount.create(0, Units.GramPerKilogram)
        )
      };
    case 3:
      return {
        [KnownProperties.openingAirTemperature]: PropertyValue.fromAmount(
          Amount.create(15, Units.Celsius)
        ),
        [KnownProperties.openingUseOutdoorAirHumidity]:
          PropertyValue.fromInteger(1),
        [KnownProperties.openingAirHumidity]: PropertyValue.fromAmount(
          Amount.create(0, Units.GramPerKilogram)
        )
      };
  }
}
// tslint:disable-next-line:max-file-line-count
